import Vue from 'vue'

/**
 * Resolve a propriedade expression do elemento e atualiza o objeto alvo com o resultado.
 * @param {Object} element - O elemento que contém a propriedade expression a ser resolvida.
 * @param {Object} target - O objeto que será modificado pela expressão.
 * @param {Object} context - O contexto que contém os parâmetros necessários para a resolução da expressão.
 * @example
 * const context = {
 *   eref: {
 *     name: 'Minha.Rota',
 *     args: {
 *       "Id": 10,
 *       "Nome": "João"
 *     },
 *     value: 'Minha.Rota(Id=10;Nome=João)'
 *   },
 *   page: this.entity,
 *   data: this.entity.dataSet.sets.[0],
 *   row: selection
 * };
 * solveExpressionProperty(element, target, context);
 */
export function solveExpressionProperty(element, target, context) {
  if (!element?.expression) return
  compileExpressionProperty(element)
  solveExpression(element.expression, target, context)
}

/**
 * Compila a propriedade expression do elemento.
 *
 * @param {object} element - O elemento a ser compilado.
 */
export function compileExpressionProperty(element) {
  if (element?.expression === 'string') {
    element.expression = compileExpression(element.expression)
  }
}

/**
 * Resolve a propriedade expression do elemento e atualiza o objeto alvo com o resultado.
 * @param {string|function} expression - A expressão a ser resolvida.
 * @param {Object} target - O objeto que será modificado pela expressão.
 * @param {Object} context - O contexto que contém os parâmetros necessários para a resolução da expressão.
 * @example
 * const context = {
 *   eref: {
 *     name: 'Minha.Rota',
 *     args: {
 *       "Id": 10,
 *       "Nome": "João"
 *     },
 *     value: 'Minha.Rota(Id=10;Nome=João)'
 *   },
 *   page: this.entity,
 *   data: this.entity.dataSet.sets.[0],
 *   row: selection
 * };
 * solveExpression(expression, target, context);
 */
export function solveExpression(expression, target, context) {
  if (typeof expression === 'string') {
    expression = compileExpression(expression)
  }
  if (typeof expression === 'function') {

    // Garantindo as variáveis de contexto.
    // Note que `args` é um atalho para `eref.args`.
    const defaultValues = {
      eref: null,
      page: null,
      data: null,
      form: null,
      row: null
    }
    const callContext = {
      ...defaultValues,
      ...context,
      ...{ args: (context.eref?.args || null) },
      ...{ $set: Vue.set }
    }

    expression.call(target, callContext)
  }
}

/**
 * Compila uma expressão em um template JavaScript e retorna uma nova função.
 *
 * @param {string} template - O template da expressão a ser compilada.
 * @returns {Function} - Uma nova função que pode ser executada com um objeto de contexto.
 */
export function compileExpression(template) {
  let expression = ''
  try {
    // Substituindo caracteres de escape por um temporário
    template = template.replace(/\\;/g, '§')

    // Transformando o template em um script javascript.
    // Exemplo, isso:
    //   name: row.name; age: data.age
    // Se torna isso:
    //   this['name'] = row.name; this['age'] = data.age;
    const pairs = template.split(';').map(pair => pair.trim()).filter(pair => pair)
    const expressions = pairs.map(pair => {
      let [property, value] = splitFirst(pair, ':').map(part => part.trim())
      value = value?.replace(/args\./g, 'args?.')
      value = value?.replace(/eref\./g, 'eref?.')
      value = value?.replace(/page\./g, 'page?.')
      value = value?.replace(/data\./g, 'data?.')
      value = value?.replace(/form\./g, 'form?.')
      value = value?.replace(/row\./g, 'row?.')
      return property ? ` context.$set(this, '${property}', ${value})` : ''
    })
    template = expressions.join(';')

    // Restaurando caracteres de escape
    template = template.replace(/§/g, ';')

    // Juntando todas as expressões em uma única string
    expression =
      ` const target = this;
        const { site, eref, args, page, data, form, row } = context;
        const val = a => a?.value || a;
        const eq = (a, b) => val(a) == val(b);
        const ne = (a, b) => val(a) != val(b);
        const gt = (a, b) => val(a) > val(b);
        const ge = (a, b) => val(a) >= val(b);
        const lt = (a, b) => val(a) < val(b);
        const le = (a, b) => val(a) <= val(b);
        const and = (a, b) => val(a) && val(b);
        const or = (a, b) => val(a) || val(b);
        const not = a => !val(a);
        const exist = a => !!val(a);
        const coalesce = (...args) => args.find(a => exist(a));
        const choose = (a, b, c) => val(a) ? val(b) : val(c);
        const concat = (...args) => args.map(val).join('');
        const isnull = a => a === undefined || a === null;
        ${template} `
    // Retornando uma nova função
    return new Function('context', expression)

  } catch (error) {
    console.warn(`A expressão não é válida: ${expression}`, error)
    return () => {}
  }
}

/**
 * Encontra o índice da primeira ocorrência do delimitador no texto.
 *
 * @param {string} text - O texto onde a busca será realizada.
 * @param {string} delimiter - O delimitador a ser encontrado.
 * @returns {number} O índice da primeira ocorrência do delimitador no texto, ou -1 se não for encontrado.
 */
function splitFirst(text, delimiter) {
  let index = text.indexOf(delimiter);
  if (index === -1) return [text]; // Caso o delimitador não seja encontrado, retorna a string original em um array.
  return [text.substring(0, index), text.substring(index + 1)];
}
